import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, switchMap, tap } from 'rxjs';
import { licenseActions } from '../actions/license.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class LicenseEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getLicense$ = createEffect(() => {
        return this.actions.pipe(
            ofType(licenseActions.getLicense),
            switchMap(({ id }) => {
                return this.httpService.getLicense(id).pipe(
                    finalizeLoader('license-get'),
                    map((license) => licenseActions.getLicenseSuccess({ payload: license })),
                    catchError((response: HttpErrorResponse) => of(licenseActions.getLicenseError({ code: response.status }))),
                );
            }),
        );
    });

    updateLicense$ = createEffect(() => {
        return this.actions.pipe(
            ofType(licenseActions.updateLicense),
            switchMap(({ id, value }) => {
                return this.httpService.updateLicense(id, value).pipe(
                    finalizeLoader('license-update'),
                    map((license) => licenseActions.updateLicenseSuccess({ payload: license })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.licenses.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.licenses.updateError');
                        return of(licenseActions.updateLicenseError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    upgradeLicense$ = createEffect(() => {
        return this.actions.pipe(
            ofType(licenseActions.upgradeLicense),
            switchMap(({ id, value }) => {
                return this.httpService.upgradeLicense(id, value).pipe(
                    finalizeLoader('license-upgrade'),
                    map((license) => licenseActions.upgradeLicenseSuccess({ payload: license })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.licenses.upgraded'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.licenses.upgradeError');
                        return of(licenseActions.upgradeLicenseError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
